import { type Expense } from "@finway-group/shared/lib/models/expense/expense.model"
import { type LineItem } from "@finway-group/shared/lib/models/expense/expenseSplit.model"
import { type InvoiceLinkRequest } from "@finway-group/shared/lib/models/expense/linkRequest.interface"
import { PurchaseOrderStatusEnum } from "@finway-group/shared/lib/models/purchaseOrder/purchaseOrderStatus.enum"
import { useEffect, useMemo, useRef, useState } from "react"
import { useAutoMatchPurchaseOrderMutation } from "~Features/pages/purchaseOrders/queries/purchaseOrder.queries"
import { type PurchaseOrderForLinking } from "~Features/pages/purchaseOrders/types/PurchaseOrder.interfaces"
import { i18n } from "~Shared/locales/i18n"
import { useLinkPurchaseOrderMutation } from "~Shared/queries/expenses/expense.queries"
import { NotificationService } from "~Shared/services"
import { diff } from "~Shared/utils/array.utils"

export type LinkedPair = InvoiceLinkRequest["links"][number] & { isAutoMatched?: boolean }

const pairSortKey = (pair: LinkedPair) => `${pair.invoiceItemId},${pair.poItemId},${pair.poId}`

export const decorateItemsWithIndex = <T>(splits: Array<T>): Array<T & { index: number }> => splits?.map((item, index) => ({ ...item, index }))

export const toAutoMatchInvoiceItem = (split: LineItem) => ({
    _id: split._id!,
    name: split.name,
    quantity: split.quantity,
    unitPrice: split.unitPrice,
    totalAmount: split.grossPrice,
    // the API only skips an absent material code, not null
    materialCode: split.materialCode || undefined,
})

export const useLinkPurchaseOrder = (expense: Expense | undefined, purchaseOrders: Array<PurchaseOrderForLinking> = []) => {
    const { mutateAsync: linkPurchaseOrder, isLoading: isLinkMutationLoading } = useLinkPurchaseOrderMutation()
    const { mutateAsync: autoMatchPurchaseOrder, isLoading: isAutoMatchLoading } = useAutoMatchPurchaseOrderMutation()
    const [isInitialAutoMatch, setIsInitialAutoMatch] = useState(true)
    const [hasNoAutoMatchResults, setHasNoAutoMatchResults] = useState(false)
    const hasAutoMatchedRef = useRef(false)
    const expenseSplits = useMemo(() => decorateItemsWithIndex((expense?.splits as Array<LineItem>) || []), [expense?.splits])
    const [activeItem, setActiveItem] = useState<string>("")
    const initialLinkedPairs: Array<LinkedPair> = useMemo(
        () =>
            expenseSplits
                .filter((item) => item.purchaseOrderLink)
                .map((item) => ({
                    invoiceItemId: item._id!,
                    poId: item.purchaseOrderLink?._id!,
                    poItemId: item.purchaseOrderLink?.itemId!,
                })),
        [expenseSplits],
    )

    const isPoLinkingDisabled = (poId: string): boolean => {
        const po = purchaseOrders.find((purchaseOrder) => purchaseOrder._id === poId)
        if (!po?.state?.currentState) {
            return true
        }
        return po.state.currentState !== PurchaseOrderStatusEnum.FULFILLMENT
    }
    const [linkedPairs, setLinkedPairs] = useState<Array<LinkedPair>>([])

    useEffect(() => {
        if (initialLinkedPairs.length > 0) {
            setLinkedPairs(initialLinkedPairs)
            setIsInitialAutoMatch(false)
        }
    }, [expense?.id])

    const hasChanges = useMemo(() => {
        if (linkedPairs.length !== initialLinkedPairs.length) {
            return true
        }
        const differences = diff(linkedPairs, initialLinkedPairs, pairSortKey)
        return differences.hasChanges
    }, [linkedPairs, initialLinkedPairs])

    const autoMatchAll = async () => {
        // Only include unlinked items from POs that are in FULFILLMENT status
        const eligiblePurchaseOrderItems = purchaseOrders
            .filter((po) => po.state?.currentState === PurchaseOrderStatusEnum.FULFILLMENT)
            .flatMap((po) =>
                po.lineItems
                    .filter((item) => !isPurchaseOrderItemLinked(po._id, item._id))
                    .map((item) => ({
                        _id: item._id,
                        poId: po._id,
                        name: item.name,
                        quantity: item.quantity,
                        unitPrice: item.unitPrice,
                        totalAmount: item.totalGrossPrice,
                        materialCode: item.materialCode || undefined,
                    })),
            )

        const eligibleInvoiceItems = expenseSplits.filter((split) => split._id != null && !isInvoiceItemLinked(split._id)).map(toAutoMatchInvoiceItem)

        if (!eligiblePurchaseOrderItems.length || !eligibleInvoiceItems.length) {
            setIsInitialAutoMatch(false)
            return
        }

        try {
            const result = await autoMatchPurchaseOrder({
                purchaseOrderLineItems: eligiblePurchaseOrderItems,
                invoiceLineItems: eligibleInvoiceItems,
            })

            // Append the AI-matched results to existing linked pairs, marking them as auto-matched
            setLinkedPairs((prev) => [...prev, ...result.links.map((link) => ({ ...link, isAutoMatched: true }))])
            setActiveItem("")

            // Track if no matches were found
            setHasNoAutoMatchResults(result.links.length === 0)
        } catch (error) {
            NotificationService.showErrorNotificationBasedOnResponseError(error, i18n.t("error:purchase_order.auto_match.title"), i18n.t("error:purchase_order.auto_match.message"))
        } finally {
            setIsInitialAutoMatch(false)
        }
    }

    // Auto-trigger matching when modal opens (only once)
    useEffect(() => {
        const shouldAutoMatch =
            !hasAutoMatchedRef.current && // Haven't auto-matched yet
            purchaseOrders.length > 0 && // Have purchase orders
            expenseSplits.length > 0 && // Have expense splits
            initialLinkedPairs.length === 0 && // No existing links
            !isAutoMatchLoading // Not already loading

        if (shouldAutoMatch) {
            hasAutoMatchedRef.current = true
            void autoMatchAll()
        }
    }, [purchaseOrders.length, expenseSplits.length, initialLinkedPairs.length])

    if (expenseSplits.length === 0) {
        return {
            expenseSplits,
            handleInvoiceItemClick: () => {},
            handlePurchaseOrderItemClick: () => {},
            isPoLinkingDisabled: () => true,
            removeLink: () => {},
            clearAllLinks: () => {},
            resetAutoMatchState: () => {},
            isInvoiceItemLinked: () => false,
            isPurchaseOrderItemLinked: () => false,
            isItemActive: () => false,
            isPurchaseOrderItemDisabled: () => false,
            saveLinkChanges: () => {},
            hasChanges: false,
            linkedPairs: [],
            activeItem: null,
            isClearAllDisabled: false,
            isLinkMutationLoading: false,
            autoMatchAll: async () => {},
            isAutoMatchLoading: false,
            isAutoMatchDisabled: true,
            isInitialAutoMatch: false,
            hasNoAutoMatchResults: false,
            hasUnlinkedItems: false,
            hasPOsWithUnlinkedItems: false,
        }
    }
    const handleInvoiceItemClick = (itemId: string) => {
        const isInvoiceLinked = linkedPairs.some((pair) => pair.invoiceItemId === itemId)
        if (isInvoiceLinked) {
            return
        }
        setActiveItem((prev) => (prev === itemId ? "" : itemId))
    }

    const handlePurchaseOrderItemClick = (itemId: string, poId: string) => {
        if (isPoLinkingDisabled(poId)) {
            return
        }
        const isPurchaseLinked = linkedPairs.some((pair) => pair.poId === poId && pair.poItemId === itemId)
        if (isPurchaseLinked) {
            return
        }

        if (!activeItem) {
            return
        }

        setLinkedPairs((prev) => [...prev, { invoiceItemId: activeItem, poItemId: itemId, poId, isAutoMatched: false }])
        setActiveItem("")
    }

    const removeLink = (invoiceItemId: string, purchaseItemId: string, poId: string) => {
        if (isPoLinkingDisabled(poId)) {
            return
        }
        setLinkedPairs((prev) => prev.filter((pair) => !(pair.invoiceItemId === invoiceItemId && pair.poItemId === purchaseItemId)))
        setHasNoAutoMatchResults(false)
    }

    const clearAllLinks = () => {
        // Check if any currently linked POs are completed
        const hasCompletedLinkedPO = linkedPairs.some((pair) => isPoLinkingDisabled(pair.poId))
        if (hasCompletedLinkedPO) {
            return
        }
        setLinkedPairs([])
        setActiveItem("")
        setHasNoAutoMatchResults(false)
    }

    const resetAutoMatchState = () => {
        setHasNoAutoMatchResults(false)
    }

    const isInvoiceItemLinked = (itemId: string | undefined) => linkedPairs.some((pair) => pair.invoiceItemId === itemId)

    const isPurchaseOrderItemLinked = (poId: string, poItemId: string | undefined) => linkedPairs.some((pair) => pair.poId === poId && pair.poItemId === poItemId)

    const isItemActive = (itemId: string | undefined) => activeItem === itemId

    const isPurchaseOrderItemDisabled = (poId: string, poItemId: string | undefined) => isPoLinkingDisabled(poId) || (!activeItem && !isPurchaseOrderItemLinked(poId, poItemId))

    const saveLinkChanges = async () => {
        const differences = diff(linkedPairs, initialLinkedPairs, pairSortKey)
        if (!differences.hasChanges) {
            return
        }

        const updatedExpense = await linkPurchaseOrder({
            expenseId: expense?.id!,
            linkRequest: {
                unlinks: differences.removed,
                links: differences.added,
            },
        })
        return updatedExpense
    }

    // Helper to check if clear all should be disabled
    const isClearAllDisabled = linkedPairs.length === 0 || linkedPairs.some((pair) => isPoLinkingDisabled(pair.poId))

    const hasEligiblePurchaseOrders = purchaseOrders.some((po) => po.state?.currentState === PurchaseOrderStatusEnum.FULFILLMENT)
    const hasUnlinkedItems = expenseSplits.some((split) => !isInvoiceItemLinked(split._id))
    const hasPOsWithUnlinkedItems = purchaseOrders.some(
        (po) => po.state?.currentState === PurchaseOrderStatusEnum.FULFILLMENT && po.lineItems.some((lineItem) => !isPurchaseOrderItemLinked(po._id, lineItem._id)),
    )
    const isAutoMatchDisabled =
        !hasEligiblePurchaseOrders || expenseSplits.length === 0 || isAutoMatchLoading || hasNoAutoMatchResults || !hasUnlinkedItems || !hasPOsWithUnlinkedItems

    return {
        expenseSplits,
        handleInvoiceItemClick,
        handlePurchaseOrderItemClick,
        removeLink,
        clearAllLinks,
        resetAutoMatchState,
        isInvoiceItemLinked,
        isPurchaseOrderItemLinked,
        isItemActive,
        isPurchaseOrderItemDisabled,
        saveLinkChanges,
        hasChanges,
        linkedPairs,
        activeItem,
        isPoLinkingDisabled,
        isClearAllDisabled,
        isLinkMutationLoading,
        autoMatchAll,
        isAutoMatchLoading,
        isAutoMatchDisabled,
        isInitialAutoMatch,
        hasNoAutoMatchResults,
        hasUnlinkedItems,
        hasPOsWithUnlinkedItems,
    }
}
